import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import SaveIcon from '@mui/icons-material/Save';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ISimulationDriver, ISimulationMixer } from './simulation.models';

type SimulationComputeMode = 'AUTO' | 'MANUAL';
type AutoStatus = 'IDLE' | 'SCHEDULED' | 'PROCESSING' | 'COMPLETE';

type Props = {
  mixer?: ISimulationMixer;
  loading?: boolean;
  editable?: boolean;
  canRecompute?: boolean;
  computeMode?: SimulationComputeMode;
  autoProcessing?: boolean;
  onSaveDrivers: (drivers: ISimulationDriver[]) => Promise<void>;
  onAutoCommit?: (drivers: ISimulationDriver[]) => Promise<void>;
  onRecompute: () => void;
  onDriversChange?: (drivers: ISimulationDriver[], dirty: boolean) => void;
};

type State = {
  drivers: ISimulationDriver[];
  error: string;
  autoStatus: AutoStatus;
};

class SimulationMixerPanel extends React.Component<Props, State> {
  private autoTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly autoDebounceMs = 750;

  state: State = {
    drivers: this.props.mixer?.drivers || [],
    error: '',
    autoStatus: 'IDLE',
  };

  componentDidUpdate(prev: Props) {
    if (prev.mixer !== this.props.mixer) {
      this.clearAutoTimer();
      this.setState({
        drivers: this.props.mixer?.drivers || [],
        error: '',
        autoStatus:
          this.props.computeMode === 'AUTO' && prev.autoProcessing
            ? 'COMPLETE'
            : 'IDLE',
      });
    }

    if (prev.computeMode !== this.props.computeMode) {
      this.clearAutoTimer();
      this.setState({ error: '', autoStatus: 'IDLE' });
    }
  }

  componentWillUnmount() {
    this.clearAutoTimer();
  }

  private clearAutoTimer = () => {
    if (this.autoTimer) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
  };

  private isBusy = (): boolean =>
    Boolean(this.props.loading || this.props.autoProcessing);

  private validateDrivers = (drivers: ISimulationDriver[]): ISimulationDriver[] =>
    drivers.filter(
      (d) =>
        !Number.isFinite(Number(d.value)) ||
        (d.minvalue !== undefined && Number(d.value) < Number(d.minvalue)) ||
        (d.maxvalue !== undefined && Number(d.value) > Number(d.maxvalue)),
    );

  private scheduleAutoCommit = () => {
    if (
      this.props.computeMode !== 'AUTO' ||
      !this.props.onAutoCommit ||
      this.props.editable === false ||
      this.isBusy()
    ) {
      return;
    }

    this.clearAutoTimer();

    const dirty = this.state.drivers.filter((d) => d.__dirty);
    if (!dirty.length) {
      this.setState({ autoStatus: 'IDLE' });
      return;
    }

    const invalid = this.validateDrivers(dirty);
    if (invalid.length) {
      this.setState({
        error: `Resolve ${invalid.length} invalid driver value(s).`,
        autoStatus: 'IDLE',
      });
      return;
    }

    this.setState({ error: '', autoStatus: 'SCHEDULED' });

    this.autoTimer = setTimeout(async () => {
      this.autoTimer = null;

      const pending = this.state.drivers.filter((d) => d.__dirty);
      if (!pending.length || !this.props.onAutoCommit) {
        this.setState({ autoStatus: 'IDLE' });
        return;
      }

      const pendingInvalid = this.validateDrivers(pending);
      if (pendingInvalid.length) {
        this.setState({
          error: `Resolve ${pendingInvalid.length} invalid driver value(s).`,
          autoStatus: 'IDLE',
        });
        return;
      }

      this.setState({ error: '', autoStatus: 'PROCESSING' });

      try {
        await this.props.onAutoCommit(pending);

        this.setState((state) => ({
          drivers: state.drivers.map((driver) => ({ ...driver, __dirty: false })),
          error: '',
          autoStatus: 'COMPLETE',
        }));

        if (this.props.onDriversChange) {
          this.props.onDriversChange(this.state.drivers, false);
        }
      } catch (error: any) {
        this.setState({
          error: error?.message || 'Automatic scenario recomputation failed.',
          autoStatus: 'IDLE',
        });
      }
    }, this.autoDebounceMs);
  };

  setDriver = (drivercode: string, value: number) => {
    const drivers = this.state.drivers.map((d) =>
      d.drivercode === drivercode
        ? { ...d, value, __dirty: true, __error: '' }
        : d,
    );

    this.setState({ drivers, error: '', autoStatus: 'IDLE' }, () => {
      if (this.props.onDriversChange) {
        this.props.onDriversChange(this.state.drivers, true);
      }
      this.scheduleAutoCommit();
    });
  };

  private getAdjustableDrivers = (): ISimulationDriver[] =>
    this.state.drivers.filter(
      (driver) =>
        driver.isadjustable === true &&
        driver.allowmanualoverride !== false,
    );

  private getChangePercent = (driver: ISimulationDriver): number | null => {
    const baseline = Number(driver.baselinevalue);
    const value = Number(driver.value ?? driver.effectivevalue ?? 0);

    if (!Number.isFinite(baseline) || baseline === 0 || !Number.isFinite(value)) {
      return null;
    }

    return ((value - baseline) / baseline) * 100;
  };

  private getSourceLabel = (driver: ISimulationDriver): string => {
    switch (String(driver.sourceclass || '').toUpperCase()) {
      case 'PUBLISHED_FORECAST':
        return 'Published Forecast';
      case 'MANUAL_OVERRIDE':
        return 'Scenario Override';
      case 'CALCULATED':
        return 'Calculated';
      default:
        return driver.sourcecode || 'Scenario Input';
    }
  };

  private getSourceColor = (
    driver: ISimulationDriver,
  ): 'default' | 'info' | 'warning' | 'success' => {
    switch (String(driver.sourceclass || '').toUpperCase()) {
      case 'PUBLISHED_FORECAST':
        return 'info';
      case 'MANUAL_OVERRIDE':
        return 'warning';
      case 'CALCULATED':
        return 'success';
      default:
        return 'default';
    }
  };

  private setDriverPercent = (driver: ISimulationDriver, percent: number) => {
    const baseline = Number(driver.baselinevalue);

    if (!Number.isFinite(baseline) || baseline === 0) return;

    const value = baseline * (1 + percent / 100);
    this.setDriver(driver.drivercode, value);
  };

  save = async () => {
    const dirty = this.state.drivers.filter((d) => d.__dirty);
    if (!dirty.length) {
      this.setState({ error: 'There are no changed drivers to save.' });
      return;
    }

    const invalid = this.validateDrivers(dirty);
    if (invalid.length) {
      this.setState({
        error: `Resolve ${invalid.length} invalid driver value(s).`,
      });
      return;
    }

    await this.props.onSaveDrivers(dirty);
    if (this.props.onDriversChange) {
      this.props.onDriversChange(this.state.drivers, false);
    }
  };

  private renderAutoStatus() {
    if (this.props.computeMode !== 'AUTO') return null;

    const processing = this.props.autoProcessing || this.state.autoStatus === 'PROCESSING';

    return (
      <Alert
        severity={this.state.autoStatus === 'COMPLETE' ? 'success' : 'info'}
        icon={processing ? <CircularProgress size={18} /> : <AutoAwesomeIcon fontSize="small" />}
      >
        {processing
          ? 'Saving driver changes and recomputing the scenario...'
          : this.state.autoStatus === 'SCHEDULED'
            ? 'Driver change detected. Automatic recomputation will begin when the control settles.'
            : this.state.autoStatus === 'COMPLETE'
              ? 'Scenario impact refreshed from the latest driver change.'
              : 'AUTO mode: valid driver changes are saved and recomputed automatically.'}
      </Alert>
    );
  }

  renderDriver(d: ISimulationDriver) {
    const value = Number(d.value ?? d.effectivevalue ?? 0);
    const min = Number(d.minvalue ?? Math.floor(value * 0.5));
    const max = Number(d.maxvalue ?? Math.ceil(value * 1.5 || 100));
    const step = Number(d.stepvalue ?? 1);
    const atLimit = value >= max || value <= min;
    const busy = this.isBusy();

    const changePercent = this.getChangePercent(d);
    const hasBaseline =
      Number.isFinite(Number(d.baselinevalue)) &&
      Number(d.baselinevalue) !== 0;

    const disabled =
      this.props.editable === false ||
      busy ||
      d.allowmanualoverride === false;

    return (
      <Grid size={{ xs: 12, md: 6 }} key={d.drivercode}>
        <Card elevation={1}>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {d.drivername}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {d.drivercode}
                    </Typography>
                    {(d.sourceclass || d.sourcecode) && (
                      <Chip
                        size="small"
                        label={this.getSourceLabel(d)}
                        color={this.getSourceColor(d)}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {value.toLocaleString()} {d.unit || ''}
                </Typography>
              </Stack>

              {hasBaseline && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Forecast baseline: {Number(d.baselinevalue).toLocaleString()}
                    {d.unit ? ` ${d.unit}` : ''}
                  </Typography>

                  {changePercent !== null && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={
                        Math.abs(changePercent) < 0.0001
                          ? 'default'
                          : changePercent > 0
                            ? 'success'
                            : 'warning'
                      }
                      label={(() => {
                        const displayPercent = Math.abs(changePercent) < 0.05 ? 0 : changePercent;
                        return `${displayPercent > 0 ? '+' : ''}${displayPercent.toFixed(1)}%`;
                      })()}
                    />
                  )}
                </Stack>
              )}

              <TextField
                size="small"
                label="Scenario Value"
                type="number"
                value={value}
                disabled={disabled}
                onChange={(e) =>
                  this.setDriver(d.drivercode, Number(e.target.value))
                }
                helperText={
                  String(d.sourceclass || '').toUpperCase() === 'PUBLISHED_FORECAST'
                    ? 'Inherited from the published Forecast; editing creates a scenario-specific override.'
                    : String(d.sourceclass || '').toUpperCase() === 'MANUAL_OVERRIDE'
                      ? 'Scenario-specific override of the governed Forecast baseline.'
                      : 'Scenario-controlled input.'
                }
              />

              {hasBaseline ? (
                <>
                  <Slider
                    value={Math.max(-50, Math.min(50, changePercent ?? 0))}
                    min={-50}
                    max={50}
                    step={0.5}
                    disabled={disabled}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(sliderValue) =>
                      `${Number(sliderValue) >= 0 ? '+' : ''}${sliderValue}%`
                    }
                    onChange={(_, sliderValue) =>
                      this.setDriverPercent(d, Number(sliderValue))
                    }
                  />

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption">-50%</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 700 }}
                    >
                      Change from Forecast
                    </Typography>
                    <Typography variant="caption">+50%</Typography>
                  </Stack>
                </>
              ) : (
                Number.isFinite(min) &&
                Number.isFinite(max) &&
                max > min &&
                Math.abs(max - min) < 1000000000 && (
                  <Slider
                    value={value}
                    min={min}
                    max={max}
                    step={step}
                    disabled={disabled}
                    onChange={(_, sliderValue) =>
                      this.setDriver(d.drivercode, Number(sliderValue))
                    }
                  />
                )
              )}

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption">Min {min}</Typography>
                <Typography
                  variant="caption"
                  color={atLimit ? 'warning.main' : 'text.secondary'}
                >
                  {atLimit ? 'At configured limit' : `Max ${max}`}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  render() {
    const dirty = this.state.drivers.some((d) => d.__dirty);
    const auto = this.props.computeMode === 'AUTO';
    const busy = this.isBusy();

    return (
      <Stack spacing={2}>
        {this.state.error && <Alert severity="warning">{this.state.error}</Alert>}

        {this.renderAutoStatus()}

        {this.props.mixer?.stale && (
          <Alert severity="warning">
            Driver or assumption inputs changed after the last computation. Save
            and recompute before review.
          </Alert>
        )}

        {!auto && (
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={this.save}
              disabled={!dirty || busy || this.props.editable === false}
              sx={{ ml: 'auto' }}
            >
              Save Driver Changes
            </Button>
            <Button
              variant="contained"
              startIcon={<CalculateIcon />}
              onClick={this.props.onRecompute}
              disabled={busy || dirty || this.props.canRecompute !== true}
            >
              Recompute Scenario
            </Button>
          </Stack>
        )}

        <Grid container spacing={2}>
          {this.getAdjustableDrivers().map((d) => this.renderDriver(d))}
        </Grid>
      </Stack>
    );
  }
}

export default SimulationMixerPanel;
